import { Component, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { take, switchMap, map, catchError, filter, startWith } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';

import { initFlowbite } from 'flowbite';

import { PharmasyncService } from './core/components/pharma-sync/service/pharmasync.service';
import { CookiesService } from './core/services/common/cookies.service';
import { CnpjService } from './core/services/common/cnpj.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false
})
export class AppComponent implements OnInit {

  showIzaBubble = signal(false);

  constructor(
    protected pharmaSyncService: PharmasyncService,
    private router: Router,
    private auth0: AuthService,
    private cookiesService: CookiesService,
    private cnpjService: CnpjService
  ) {
    initFlowbite();
  }

  ngOnInit(): void {
    this.cnpjService.initCnpjFromUrl();

    // Implementa SSO - detecta automaticamente se o usuário já está autenticado
    // em outra aplicação que usa o mesmo Auth0
    // this.checkSsoAuthentication();

    combineLatest([
      this.auth0.isAuthenticated$,
      this.router.events.pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(null)
      ),
    ])
      .pipe(map(([isAuth]) => isAuth && !this.isAuthRoute(this.router.url)))
      .subscribe((v) => this.showIzaBubble.set(v));
  }

  isAuthRoute(url: string): boolean {
    // login lives at path '' (router.url === '/'); cadastro at '/cadastro'.
    // Exact match on the path (query/fragment stripped) so '/logistica' etc. don't count.
    const path = url.split(/[?#]/)[0];
    return path === '/' || path === '' || path === '/cadastro';
  }

  /**
   * Verifica se existe uma sessão ativa no Auth0 (SSO).
   * Se o usuário estiver logado em outra aplicação com o mesmo Auth0,
   * ele será autenticado automaticamente aqui.
   */
  private checkSsoAuthentication(): void {
    // Primeiro verifica se já está autenticado localmente
    this.auth0.isAuthenticated$
      .pipe(
        take(1),
        switchMap((isAuthenticated) => {
          if (isAuthenticated) {
            // Já autenticado localmente, apenas salva o token
            console.log('✅ [SSO] Usuário já autenticado localmente');
            return this.auth0.idTokenClaims$.pipe(
              take(1),
              map((claims) => ({ success: true, claims }))
            );
          } else {
            // Não autenticado localmente, tenta Silent Authentication
            console.log('🔍 [SSO] Verificando sessão no Auth0...');
            return this.attemptSilentAuthentication();
          }
        }),
        catchError((error) => {
          console.log('ℹ️ [SSO] Nenhuma sessão ativa, usuário precisa fazer login');
          console.error('Erro:', error);
          return of({ success: false, error });
        })
      )
      .subscribe({
        next: (result: any) => {
          if (result.success && result.claims) {
            // Salva o token em cookie
            if (result.claims.__raw) {
              this.cookiesService.setToken(result.claims.__raw);
              console.log('✅ [SSO] Token salvo, autenticação completa');
            }
          }
        },
        error: (error) => {
          console.log('❌ [SSO] Falha na autenticação silenciosa');
          console.error(error);
        }
      });
  }

  /**
   * Tenta fazer autenticação silenciosa (Silent Authentication).
   * Se o usuário tiver uma sessão ativa no Auth0, receberá um token automaticamente.
   */
  private attemptSilentAuthentication() {
    return this.auth0.getAccessTokenSilently({
      authorizationParams: {
        redirect_uri: window.location.origin
      },
      cacheMode: 'off' // Força checagem no Auth0, não usa cache
    }).pipe(
      take(1),
      switchMap(() => {
        // Se getAccessTokenSilently funcionar, busca os claims do token
        console.log('✅ [SSO] Sessão ativa encontrada no Auth0');
        return this.auth0.idTokenClaims$.pipe(
          take(1),
          map((claims) => ({ success: true, claims }))
        );
      }),
      catchError((error) => {
        // Falha significa que não há sessão ativa no Auth0
        console.log('ℹ️ [SSO] Nenhuma sessão ativa no Auth0');
        return of({ success: false, error });
      })
    );
  }

  /**
   * Rotas públicas não renderizam o header logado: ele depende de usuário
   * autenticado e de CNPJ selecionado, que numa página anônima não existem.
   */
  shouldShowHeader(): boolean {
    const baseUrl = this.router.url.split('?')[0];

    return baseUrl !== `/` &&
      baseUrl !== `/cadastro` &&
      baseUrl !== `/diagnostico-pdv`;
  }
}
